import { useState, useMemo } from 'react';
import { Search, Plus, X, GripVertical } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { CURRENCIES } from '../../lib/constants';
import { getCurrencyFlag, getCurrencyName } from '../../lib/formatters';

interface CurrencySelectorProps {
  favorites: string[];
  onAddFavorite: (currency: string) => void;
  onRemoveFavorite: (currency: string) => void;
  onReorderFavorites: (currencies: string[]) => void;
  onSave?: () => void;
  hasUnsavedChanges?: boolean;
}

export function CurrencySelector({
  favorites,
  onAddFavorite,
  onRemoveFavorite,
  onReorderFavorites,
  onSave,
  hasUnsavedChanges = false
}: CurrencySelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const availableCurrencies = useMemo(() => {
    return CURRENCIES.filter(currency => !favorites.includes(currency.code));
  }, [favorites]);

  const filteredCurrencies = useMemo(() => {
    if (!searchQuery) return availableCurrencies;
    
    const query = searchQuery.toLowerCase();
    return availableCurrencies.filter(currency =>
      currency.code.toLowerCase().includes(query) ||
      currency.name.toLowerCase().includes(query)
    );
  }, [availableCurrencies, searchQuery]);

  const handleAddCurrency = (currencyCode: string) => {
    onAddFavorite(currencyCode);
    setSearchQuery('');
  };

  const handleRemoveCurrency = (currencyCode: string) => {
    onRemoveFavorite(currencyCode);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newFavorites = [...favorites];
    const draggedItem = newFavorites[draggedIndex];
    newFavorites.splice(draggedIndex, 1);
    newFavorites.splice(dropIndex, 0, draggedItem);
    
    onReorderFavorites(newFavorites);
    setDraggedIndex(null);
  };

  return (
    <div className="space-y-4">
      {/* Header with Save Button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Manage Currencies
        </h3>
        {onSave && (
          <Button
            size="sm"
            onClick={onSave}
            disabled={!hasUnsavedChanges}
            className={hasUnsavedChanges ? 'animate-pulse' : ''}
          >
            {hasUnsavedChanges ? 'Save Changes' : 'Saved'}
          </Button>
        )}
      </div>

      {/* Current Favorites */}
      <div>
        <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
          Your Currencies ({favorites.length})
        </h4>
        
        <div className="space-y-2">
          {favorites.map((currency, index) => (
            <div
              key={currency}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
              className={`
                flex items-center space-x-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800
                cursor-move hover:border-gray-300 dark:hover:border-gray-600 transition-colors
                ${draggedIndex === index ? 'opacity-50' : ''}
              `}
            >
              <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="text-xl">{getCurrencyFlag(currency)}</span>
              <div className="flex-1">
                <div className="font-medium text-gray-900 dark:text-white">
                  {currency}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {getCurrencyName(currency)}
                </div>
              </div>
              <button
                onClick={() => handleRemoveCurrency(currency)}
                className="p-1 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                title="Remove currency"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Add Currency */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-md font-medium text-gray-900 dark:text-white">
            Add Currency
          </h4>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsOpen(!isOpen)}
            leftIcon={<Plus className="w-4 h-4" />}
          >
            Add
          </Button>
        </div>

        {isOpen && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 p-4">
            <Input
              placeholder="Search currencies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
              className="mb-3"
            />

            <div className="max-h-60 overflow-y-auto space-y-1">
              {filteredCurrencies.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                  {searchQuery ? 'No currencies found' : 'All available currencies added'}
                </p>
              ) : (
                filteredCurrencies.map((currency) => (
                  <button
                    key={currency.code}
                    onClick={() => handleAddCurrency(currency.code)}
                    className="w-full flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
                  >
                    <span className="text-lg">{currency.flag}</span>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {currency.code}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {currency.name}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}